import type { TrainingProfile } from '../schema/profile';

const t = (cs: string, en: string) => ({ 'cs-CZ': cs, 'en-US': en });

/**
 * Práce na elektrickém zařízení - V1 DRAFT - authored for structural
 * completeness; NOT a substitute for sign-off by a Czech osoba odborně
 * způsobilá v prevenci rizik. Legal refs per the Czech BOZP research
 * (docs/specs/2026-06-23-bozp-research-czech-legal.md).
 *
 * Czech-only (LocalizedText cs-CZ). 3 chapters (bezpečné odpojení/zajištění,
 * úraz el. proudem, elektrický oblouk) + a graded quiz with one question per
 * kind. Answer keys live here (repo-privileged) and in the DB; stripped from
 * the client payload by the API.
 */
export const liveElectricalProfile: TrainingProfile = {
  slug: 'live-electrical',
  // Top-level title is a PLAIN STRING - seeded verbatim into the varchar
  // bozp_hazard_profiles.title column. Chapter titles below stay LocalizedText.
  title: 'Práce na elektrickém zařízení',
  passThreshold: 80,
  content: {
    schemaVersion: 1,
    chapters: [
      {
        id: 'ch-odpojeni',
        title: t('Bezpečné odpojení a zajištění', 'Safe isolation and lockout'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['ord-pet-pravidel'],
        blocks: [
          {
            id: 'h-odpojeni',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t(
              'Bezpečné odpojení a zajištění zařízení',
              'Safe isolation and lockout of equipment',
            ),
          },
          {
            id: 'p-odpojeni-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Práce na elektrickém zařízení se zásadně provádí **ve stavu bez napětí**. Práce pod napětím je výjimečná a smí ji vykonávat jen osoba s odpovídající odbornou způsobilostí na základě stanoveného pracovního postupu.',
              'Work on electrical equipment is, as a rule, carried out in the **de-energised state**. Live work is exceptional and may be performed only by a person with the appropriate professional competence, following an established work procedure.',
            ),
          },
          {
            id: 'anim-pet-pravidel',
            blockType: 'animation',
            schemaVersion: 1,
            scene: 'lockoutTagout',
            steps: [
              {
                label: t('Vypni', 'Switch off'),
                say: t(
                  'Odpoj zařízení od všech zdrojů napětí.',
                  'Disconnect from every source of voltage.',
                ),
              },
              {
                label: t('Zajisti', 'Lock out'),
                say: t(
                  'Zajisti proti opětovnému zapnutí - zámek a visačka.',
                  'Secure against re-energising - lock and tag.',
                ),
              },
              {
                label: t('Ověř', 'Test dead'),
                say: t('Zkoušečkou ověř beznapěťový stav.', 'Verify the dead state with a tester.'),
              },
              {
                label: t('Uzemni', 'Ground'),
                say: t('Uzemni a zkratuj.', 'Earth and short-circuit.'),
              },
              {
                label: t('Ohraď', 'Guard live parts'),
                say: t(
                  'Sousední živé části zakryj nebo ohraď.',
                  'Cover or fence off neighbouring live parts.',
                ),
              },
            ],
            caption: t(
              'Pět bezpečnostních pravidel: vypni - zajisti - ověř beznapěťový stav - uzemni - ohraď.',
              'The five safety rules: isolate - secure - verify the de-energised state - earth - barrier off.',
            ),
          },
          {
            id: 'c-odpojeni-pravo',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'info',
            markdown: t(
              'Právní a normativní základ: **NV 194/2022 Sb.** (odborná způsobilost v elektrotechnice) a **ČSN EN 50110-1** (obsluha a práce na elektrických zařízeních). Odbornou způsobilost vyžaduje rovněž **vyhl. 180/2015 Sb. §5(2)(c)** pro mladistvé.',
              'Legal and normative basis: **Gov. Reg. 194/2022 Coll.** (professional competence in electrical engineering) and **ČSN EN 50110-1** (operation of and work on electrical installations). Professional competence is also required by **Decree 180/2015 Coll. §5(2)(c)** for young workers.',
            ),
          },
          {
            id: 'p-odpojeni-2',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Před zahájením práce bez napětí se postupuje podle **pěti bezpečnostních pravidel** podle ČSN EN 50110-1. Pořadí kroků je závazné - beznapěťový stav nelze předpokládat, dokud není ověřen.',
              'Before starting de-energised work, follow the **five safety rules** per ČSN EN 50110-1. The order of the steps is mandatory - the de-energised state must never be assumed until it has been verified.',
            ),
          },
          {
            id: 'ord-pet-pravidel',
            blockType: 'ordering',
            schemaVersion: 1,
            prompt: t(
              'Seřaď pět bezpečnostních pravidel při práci bez napětí do správného pořadí.',
              'Put the five safety rules for de-energised work into the correct order.',
            ),
            items: [
              {
                id: 'pp-1',
                label: t(
                  'Odpojit zařízení ze všech stran',
                  'Disconnect the equipment on all sides',
                ),
              },
              {
                id: 'pp-2',
                label: t('Zajistit proti opětovnému zapnutí', 'Secure against re-energisation'),
              },
              { id: 'pp-3', label: t('Ověřit beznapěťový stav', 'Verify the de-energised state') },
              { id: 'pp-4', label: t('Uzemnit a zkratovat', 'Earth and short-circuit') },
              {
                id: 'pp-5',
                label: t(
                  'Zakrýt nebo ohradit sousední živé části',
                  'Cover or barrier off adjacent live parts',
                ),
              },
            ],
          },
          {
            id: 'k-odpojeni',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'Beznapěťový stav **vždy ověř měřením** a zajisti zařízení proti opětovnému zapnutí. Bez ověření nepředpokládej, že je zařízení bez napětí.',
              '**Always verify** the de-energised state by measurement and secure the equipment against re-energisation. Never assume the equipment is dead without verifying it.',
            ),
          },
        ],
      },
      {
        id: 'ch-uraz',
        title: t('Úraz elektrickým proudem', 'Electric shock injury'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['kc-uraz'],
        blocks: [
          {
            id: 'h-uraz',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Úraz elektrickým proudem', 'Electric shock injury'),
          },
          {
            id: 'p-uraz-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Průchod elektrického proudu tělem může způsobit **svalové křeče, popáleniny, zástavu dechu i fibrilaci srdce**. Závažnost závisí na velikosti proudu, době působení a dráze proudu tělem.',
              'The passage of electric current through the body can cause **muscle spasms, burns, respiratory arrest and cardiac fibrillation**. The severity depends on the magnitude of the current, the duration of exposure and the current path through the body.',
            ),
          },
          {
            id: 'c-uraz-danger',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'danger',
            markdown: t(
              'Za bezpečné se v normálních podmínkách považuje dotykové napětí do **50 V AC** (resp. 120 V DC). Doplňkovou ochranu zajišťuje **proudový chránič (RCD) se jmenovitým reziduálním proudem 30 mA**.',
              'Under normal conditions, a touch voltage of up to **50 V AC** (or 120 V DC) is considered safe. Additional protection is provided by a **residual-current device (RCD) with a rated residual current of 30 mA**.',
            ),
          },
          {
            id: 'cl-uraz',
            blockType: 'checklist',
            schemaVersion: 1,
            items: [
              t(
                'Před prací ověř beznapěťový stav měřením',
                'Before work, verify the de-energised state by measurement',
              ),
              t(
                'Používej izolované nářadí a OOPP odpovídající napětí',
                'Use insulated tools and PPE rated for the voltage',
              ),
              t(
                'Zkontroluj funkčnost proudového chrániče (tlačítko TEST)',
                'Check that the RCD works (TEST button)',
              ),
              t(
                'Vlhké prostředí zvyšuje riziko - sniž dovolené napětí',
                'A damp environment increases the risk - lower the permitted voltage',
              ),
            ],
          },
          {
            id: 'kc-uraz',
            blockType: 'knowledgeCheck',
            schemaVersion: 1,
            prompt: t(
              'Jaký jmenovitý reziduální proud má proudový chránič pro ochranu osob?',
              'What rated residual current does an RCD for personal protection have?',
            ),
            choices: [
              { id: 'kc-a', label: t('30 mA', '30 mA') },
              { id: 'kc-b', label: t('300 mA', '300 mA') },
              { id: 'kc-c', label: t('30 A', '30 A') },
            ],
            correctChoiceId: 'kc-a',
            explanation: t(
              'Proudový chránič 30 mA odpojí obvod dříve, než proud dosáhne hodnot nebezpečných pro srdce.',
              'A 30 mA RCD disconnects the circuit before the current reaches levels dangerous to the heart.',
            ),
          },
          {
            id: 'acc-uraz',
            blockType: 'accordion',
            schemaVersion: 1,
            items: [
              {
                q: t('Proč je vlhko nebezpečné?', 'Why is moisture dangerous?'),
                a: t(
                  'Voda a pot snižují odpor lidského těla, takže i nižší napětí může způsobit nebezpečný proud. Ve vlhkém prostředí klesá hranice bezpečného napětí.',
                  'Water and sweat lower the resistance of the human body, so even a lower voltage can drive a dangerous current. In a damp environment the safe-voltage limit drops.',
                ),
              },
              {
                q: t('Jak postupovat při úrazu?', 'How to act in the event of an injury?'),
                a: t(
                  'Nejprve bezpečně odpoj zdroj napětí, pak přivolej ZZS na lince 155, zahaj resuscitaci a sleduj životní funkce.',
                  'First safely disconnect the source of voltage, then call the emergency medical service on 155, begin resuscitation and monitor vital signs.',
                ),
              },
            ],
          },
        ],
      },
      {
        id: 'ch-oblouk',
        title: t('Elektrický oblouk', 'Electric arc'),
        minDwellSeconds: 20,
        blocks: [
          {
            id: 'h-oblouk',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Elektrický oblouk (arc flash)', 'Electric arc (arc flash)'),
          },
          {
            id: 'p-oblouk-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Elektrický oblouk vzniká například při zkratu nebo chybné manipulaci. Plazma oblouku dosahuje teplot v řádu **tisíců °C** a způsobuje těžké popáleniny, tlakovou vlnu a oslepení. Ohrožen je i pracovník stojící poblíž bez přímého kontaktu se zařízením.',
              'An electric arc arises, for example, during a short circuit or faulty operation. The arc plasma reaches temperatures on the order of **thousands of °C** and causes severe burns, a pressure wave and blindness. Even a worker standing nearby without direct contact with the equipment is at risk.',
            ),
          },
          {
            id: 'c-oblouk-warn',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'warning',
            markdown: t(
              '**Nikdy nepoužívej syntetický oděv** - při oblouku se taví a přilne ke kůži. Používej oděv odolný proti oblouku (arc-rated), obličejový štít a izolační rukavice.',
              '**Never wear synthetic clothing** - in an arc it melts and sticks to the skin. Wear arc-rated clothing, a face shield and insulating gloves.',
            ),
          },
          {
            id: 'cl-oblouk',
            blockType: 'checklist',
            schemaVersion: 1,
            items: [
              t(
                'Oděv odolný proti oblouku (arc-rated), nehořlavý',
                'Arc-rated, flame-resistant clothing',
              ),
              t(
                'Obličejový štít s odpovídající ochranou',
                'Face shield with appropriate protection',
              ),
              t(
                'Izolační rukavice odpovídající napěťové třídy',
                'Insulating gloves of the appropriate voltage class',
              ),
              t(
                'Dodržení bezpečné vzdálenosti a ohrazení pracoviště',
                'Keeping a safe distance and barriering off the workplace',
              ),
            ],
          },
          {
            id: 'k-oblouk',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'Oblouk ohrožuje i bez přímého dotyku. Spoléhej na **odpojení a zajištění** zařízení, ne na blízkost živých částí. Tísňová linka: **155**.',
              'An arc is a hazard even without direct contact. Rely on **isolating and securing** the equipment, not on proximity to live parts. Emergency line: **155**.',
            ),
          },
        ],
      },
    ],
  },
  questions: [
    {
      id: 'q-napeti',
      kind: 'numeric',
      chapterId: 'ch-uraz',
      prompt: t(
        'Jaké je v normálních podmínkách nejvyšší bezpečné dotykové napětí střídavé (AC) ve voltech?',
        'Under normal conditions, what is the highest safe AC touch voltage, in volts?',
      ),
      correct: 50,
      tolerance: 0,
    },
    {
      id: 'q-prvni-pravidlo',
      kind: 'single',
      chapterId: 'ch-odpojeni',
      prompt: t(
        'Které je první z pěti bezpečnostních pravidel při práci bez napětí?',
        'Which is the first of the five safety rules for de-energised work?',
      ),
      choices: [
        { id: 'a', label: t('Uzemnit a zkratovat', 'Earth and short-circuit') },
        {
          id: 'b',
          label: t('Odpojit zařízení ze všech stran', 'Disconnect the equipment on all sides'),
        },
        { id: 'c', label: t('Ověřit beznapěťový stav', 'Verify the de-energised state') },
      ],
      correct: 'b',
    },
    {
      id: 'q-syntetika-tf',
      kind: 'truefalse',
      chapterId: 'ch-oblouk',
      prompt: t(
        'Syntetický oděv dostatečně chrání před účinky elektrického oblouku.',
        'Synthetic clothing provides sufficient protection against the effects of an electric arc.',
      ),
      correct: false,
    },
    {
      id: 'q-uraz-multi',
      kind: 'multi',
      chapterId: 'ch-uraz',
      prompt: t(
        'Které faktory ovlivňují závažnost úrazu elektrickým proudem? (vyber všechny správné)',
        'Which factors affect the severity of an electric shock injury? (select all that apply)',
      ),
      choices: [
        { id: 'a', label: t('Velikost proudu', 'The magnitude of the current') },
        { id: 'b', label: t('Doba působení', 'The duration of exposure') },
        { id: 'c', label: t('Dráha proudu tělem', 'The current path through the body') },
        { id: 'd', label: t('Barva izolace vodiče', 'The colour of the conductor insulation') },
      ],
      correct: ['a', 'b', 'c'],
    },
    {
      id: 'q-pravidla-order',
      kind: 'ordering',
      chapterId: 'ch-odpojeni',
      prompt: t(
        'Seřaď bezpečnostní pravidla práce bez napětí do správného pořadí.',
        'Put the safety rules for de-energised work into the correct order.',
      ),
      choices: [
        { id: 'a', label: t('Odpojit', 'Disconnect') },
        { id: 'b', label: t('Zajistit proti zapnutí', 'Secure against switching on') },
        { id: 'c', label: t('Ověřit beznapěťový stav', 'Verify the de-energised state') },
        { id: 'd', label: t('Uzemnit a zkratovat', 'Earth and short-circuit') },
        { id: 'e', label: t('Zakrýt sousední živé části', 'Cover adjacent live parts') },
      ],
      correct: ['a', 'b', 'c', 'd', 'e'],
    },
  ],
};
